import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ModalClose } from '../../../components';

type MappingCommentFormProps<
  T extends { uuid: string; name: string; comment?: string },
> = {
  title: string;
  data: T[];
  onSave: (uuid: string, updatedComment: string) => void;
};

const MappingCommentForm = <
  T extends { uuid: string; name: string; comment?: string },
>({
  title,
  data,
  onSave,
}: MappingCommentFormProps<T>) => {
  const [editingFields, setEditingFields] = useState<Record<string, boolean>>(
    Object.fromEntries(data.map((item) => [item.uuid, false])),
  );

  const { control, handleSubmit, getValues } = useForm();

  const handleSave = (uuid: string) => {
    const updatedComment = getValues(`data.${uuid}.comment`);
    onSave(uuid, updatedComment);
    setEditingFields((prev) => ({ ...prev, [uuid]: false }));
  };

  return (
    <div>
      <h2 className="font-bold text-lg tracking-widest mb-4">{title}</h2>
      <div className="flex flex-col gap-4">
        {data?.map((item) => (
          <section key={item.uuid} className="relative grid grid-cols-2">
            <h3 className="font-semibold text-primary-200 text-sm">
              {item.name}
            </h3>
            <div>
              <Controller
                control={control}
                defaultValue={item.comment}
                name={`data.${item.uuid}.comment`}
                render={({ field }) => (
                  <TextArea
                    autoSize={{
                      minRows: !editingFields[item.uuid] ? 2 : 4,
                      maxRows: !editingFields[item.uuid] ? 2 : 6,
                    }}
                    readOnly={!editingFields[item.uuid]}
                    className="rounded-lg mr-8 w-full"
                    id={`comment-${item.uuid}`}
                    {...field}
                  />
                )}
              />
              <div className="bg-white absolute top-2 right-2">
                {!editingFields[item.uuid] ? (
                  <EditOutlined
                    className="text-blue hover:cursor-pointer"
                    onClick={() =>
                      setEditingFields((prev) => ({
                        ...prev,
                        [item.uuid]: true,
                      }))
                    }
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <SaveOutlined
                      className="text-blue hover:cursor-pointer"
                      onClick={() =>
                        handleSubmit(() => handleSave(item.uuid))()
                      }
                    />
                    <ModalClose
                      className="w-3 h-3 fill-blue hover:cursor-pointer"
                      onClick={() =>
                        setEditingFields((prev) => ({
                          ...prev,
                          [item.uuid]: false,
                        }))
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default MappingCommentForm;
